import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import { skillsApi, UserSkill, Skill } from '../../lib/api/skills'

export const Route = createFileRoute('/dashboard/skills')({
  component: SkillsComponent,
})

function SkillsComponent() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSkill, setEditingSkill] = useState<UserSkill | null>(null)
  const queryClient = useQueryClient()

  // Fetch user's skills
  const { data, isLoading, error } = useQuery({
    queryKey: ['userSkills'],
    queryFn: skillsApi.getUserSkills,
  })

  // Fetch all available skills
  const { data: allSkills } = useQuery({
    queryKey: ['allSkills'],
    queryFn: skillsApi.getAll,
  })

  // Add mutation
  const addMutation = useMutation({
    mutationFn: skillsApi.addUserSkill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSkills'] })
      setIsModalOpen(false)
      setEditingSkill(null)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      skillsApi.updateUserSkill(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSkills'] })
      setIsModalOpen(false)
      setEditingSkill(null)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: skillsApi.deleteUserSkill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSkills'] })
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      skill_id: Number(formData.get('skill_id')),
      proficiency_level: Number(formData.get('proficiency_level')),
      years_of_experience: Number(formData.get('years_of_experience')),
    }

    if (editingSkill) {
      updateMutation.mutate({ id: editingSkill.id, data })
    } else {
      addMutation.mutate(data)
    }
  }

  const handleEdit = (skill: UserSkill) => {
    setEditingSkill(skill)
    setIsModalOpen(true)
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to remove this skill?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingSkill(null)
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">My Skills</h1>
            <p className="text-base-content/70 mt-1">
              Manage your professional skills and expertise
            </p>
          </div>
          <button
            className="btn btn-accent text-white"
            onClick={() => setIsModalOpen(true)}
          >
            Add Skill
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <p className="text-center py-8">Loading skills...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="alert alert-error">
            <span>Error: {error.message}</span>
          </div>
        )}

        {/* Skills List */}
        {data && data.skills && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.skills.map((userSkill: any) => (
              <div key={userSkill.id} className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">{userSkill.skill.name}</h2>
                  <p className="text-sm text-base-content/70">{userSkill.skill.category}</p>
                  <p className="text-sm mt-2">{userSkill.skill.description}</p>
                  
                  <div className="divider"></div>
                  
                  <div className="flex justify-between text-sm">
                    <div>
                      <span className="font-semibold">Proficiency:</span>
                      <div className="badge badge-accent ml-2">{userSkill.proficiency_level}/10</div>
                    </div>
                    <div>
                      <span className="font-semibold">Experience:</span>
                      <span className="ml-2">{userSkill.years_of_experience} years</span>
                    </div>
                  </div>

                  <div className="card-actions justify-end mt-4">
                    <button
                      className="btn btn-sm btn-ghost"
                      onClick={() => handleEdit(userSkill)}
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      className="btn btn-sm btn-ghost text-error"
                      onClick={() => handleDelete(userSkill.id)}
                      disabled={deleteMutation.isPending}
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {data && data.skills && data.skills.length === 0 && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <p className="text-center text-base-content/50 py-8">
                No skills added yet. Click "Add Skill" to get started!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              {editingSkill ? 'Edit Skill' : 'Add New Skill'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingSkill && (
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Skill *</span>
                  </label>
                  <select
                    name="skill_id"
                    className="select select-bordered"
                    required
                  >
                    <option value="">Select a skill</option>
                    {allSkills?.skills.map((skill: Skill) => (
                      <option key={skill.id} value={skill.id}>
                        {skill.name} ({skill.category})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editingSkill && (
                <div className="alert alert-info">
                  <span>
                    Editing: <strong>{editingSkill.skill.name}</strong>
                  </span>
                </div>
              )}

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Proficiency Level (1-10) *</span>
                </label>
                <input
                  type="number"
                  name="proficiency_level"
                  className="input input-bordered"
                  min="1"
                  max="10"
                  required
                  defaultValue={editingSkill?.proficiency_level || 5}
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Years of Experience *</span>
                </label>
                <input
                  type="number"
                  name="years_of_experience"
                  className="input input-bordered"
                  min="0"
                  step="0.5"
                  required
                  defaultValue={editingSkill?.years_of_experience || 0}
                />
              </div>

              <div className="modal-action">
                <button
                  type="button"
                  className="btn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-accent text-white"
                  disabled={addMutation.isPending || updateMutation.isPending}
                >
                  {addMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingSkill
                    ? 'Update'
                    : 'Add'}
                </button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={handleCloseModal}>close</button>
          </form>
        </dialog>
      )}
    </>
  )
}
